import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, ShoppingCart } from 'lucide-react';
import { getOrders, getOrder } from '../../services/brokerageService';
import type { Order } from '../../services/brokerageService';

// ── Status badge colours ────────────────────────────────────────────────────

function statusBadge(status: string): string {
  switch (status) {
    case 'pending':   return 'bg-yellow-900/50 text-yellow-400 border border-yellow-700';
    case 'filled':    return 'bg-green-900/50 text-green-400 border border-green-700';
    case 'rejected':  return 'bg-red-900/50 text-red-400 border border-red-700';
    case 'cancelled': return 'bg-slate-700 text-slate-400';
    default:          return 'bg-slate-700 text-slate-400';
  }
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-ZA', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ── Pending order poller ────────────────────────────────────────────────────

const PendingOrderPoller: React.FC<{ order: Order }> = ({ order }) => {
  const queryClient = useQueryClient();

  const { data: detail } = useQuery({
    queryKey: ['brokerage', 'orders', order.order_id],
    queryFn: () => getOrder(order.order_id),
    enabled: order.status === 'pending',
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (detail && detail.status !== 'pending') {
      queryClient.invalidateQueries({ queryKey: ['brokerage', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['brokerage', 'account'] });
    }
  }, [detail?.status, queryClient]);

  return null; // renders nothing, just handles polling side effects
};

// ── Main component ──────────────────────────────────────────────────────────

interface OrderHistoryProps {
  onPlaceOrder?: () => void;
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ onPlaceOrder }) => {
  const [page, setPage] = useState(1);

  const { data: orders, isLoading, isError, error } = useQuery({
    queryKey: ['brokerage', 'orders', page],
    queryFn: () => getOrders(page, 20),
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 py-8 text-slate-400 text-sm">
        <Loader2 size={18} className="animate-spin text-teal-400" />
        Loading orders…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 py-6 text-red-400 text-sm">
        <AlertCircle size={16} />
        <span>Failed to load orders: {(error as Error)?.message ?? 'Unknown error'}</span>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ShoppingCart size={40} className="text-slate-600 mb-4" />
        <p className="text-slate-300 font-medium mb-1">No orders yet</p>
        <p className="text-slate-500 text-sm mb-4">Place your first order to get started</p>
        {onPlaceOrder && (
          <button
            onClick={onPlaceOrder}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Place your first order
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Pending pollers (invisible, side-effect only) */}
      {orders
        .filter((o) => o.status === 'pending')
        .map((o) => (
          <PendingOrderPoller key={o.order_id} order={o} />
        ))}

      {/* Order table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-700/50">
              <th className="text-left py-2 pr-4">Symbol</th>
              <th className="text-left py-2 pr-4">Side</th>
              <th className="text-left py-2 pr-4">Type</th>
              <th className="text-right py-2 pr-4">Qty</th>
              <th className="text-right py-2 pr-4">Limit</th>
              <th className="text-left py-2 pr-4">Status</th>
              <th className="text-left py-2">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr
                key={order.order_id}
                className="border-b border-slate-700/30 hover:bg-slate-800/30 transition-colors"
              >
                <td className="py-3 pr-4 font-medium text-slate-100">{order.symbol}</td>
                <td className="py-3 pr-4">
                  <span
                    className={`font-semibold ${
                      order.side === 'buy' ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {order.side.toUpperCase()}
                  </span>
                </td>
                <td className="py-3 pr-4 text-slate-400 capitalize">{order.order_type}</td>
                <td className="py-3 pr-4 text-right text-slate-300">{order.quantity}</td>
                <td className="py-3 pr-4 text-right text-slate-400">
                  {order.limit_price != null ? order.limit_price.toFixed(2) : '—'}
                </td>
                <td className="py-3 pr-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${statusBadge(order.status)}`}>
                    {order.status}
                  </span>
                </td>
                <td className="py-3 text-slate-500 text-xs">{formatDate(order.submitted_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 rounded-lg transition-colors"
        >
          Previous
        </button>
        <span className="text-slate-500 text-sm">Page {page}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={orders.length < 20}
          className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 rounded-lg transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default OrderHistory;
